import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Swal from 'sweetalert2';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import PageHeader from '../../components/ui/PageHeader';
import { HOME_CRUMB } from '../../components/ui/Breadcrumbs';
import { MERGE_FIELDS, PitchBodyTemplate, DEFAULT_PITCH_BLOCK_FIELDS, REQUIRED_PITCH_BODY_FIELD_KEYS } from '../../types/email';
import { handleError } from '../../utils/errorHandler';
import getPitchDataHtml from '../../components/tabs/email/templates/PitchMergeFields';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';
import type { DeltaStatic } from 'quill';
import templateApi from '../../repositories/template';
import pitchBodiesApi from '../../repositories/pitchBodies';
import { useAppSelector } from '../../hooks/redux';
import { fromBase64, toBase64 } from '../../utils/base64';
import { Skeleton, SkeletonCard } from '../../components/ui/skeleton';
import Button from '../../components/ui/Button';

const validationSchema = Yup.object({
  name: Yup.string()
    .required('Template name is required')
    .max(50, 'Template name must be 50 characters or less'),
  // greetingDelta: Yup.required('Greeting text is required'),
  // closingDelta: Yup.required('Closing text is required'),
});

// Convert Quill Delta to HTML with inline styles (email-ready)
function deltaToHtml(delta: DeltaStatic | null): string {
  if (!delta?.ops) return '';
  const converter = new QuillDeltaToHtmlConverter(delta.ops, {
    inlineStyles: true, // Converts to inline styles instead of classes
  });
  return converter.convert();
}

const quillModules = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      [{ size: ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean'],
    ],
  },
};

const quillFormats = [
  'header',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'align',
  'list',
  'bullet',
  'link',
];

const TemplateEditor: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = user?.role === 'admin';

  const [loading, setLoading] = useState(isEditing);
  const [activeField, setActiveField] = useState<'greetingText' | 'closingText'>('greetingText');
  const [pitchBodyTemplates, setPitchBodyTemplates] = useState<PitchBodyTemplate[]>([]);
  const [selectedPitchBodyId, setSelectedPitchBodyId] = useState<string | null>(null);
  const greetingQuillRef = useRef<ReactQuill>(null);
  const closingQuillRef = useRef<ReactQuill>(null);

  const formik = useFormik({
    initialValues: {
      name: '',
      isDefault: false,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        // Get Delta from editors
        const greetingDelta = greetingQuillRef.current?.getEditor()?.getContents() || null;
        const closingDelta = closingQuillRef.current?.getEditor()?.getContents() || null;

        // Convert Delta to inline-styled HTML for backend
        const selectedBody = pitchBodyTemplates.find((pb) => pb.id === selectedPitchBodyId);
        const pitchBlockHtml = selectedBody
          ? `<div>${selectedBody.fields
              .filter((f) => f.enabled && !(REQUIRED_PITCH_BODY_FIELD_KEYS as readonly string[]).includes(f.key))
              .sort((a, b) => a.order - b.order)
              .map((f) => `<p style="margin:0 0 8px 0;"><strong><u>${f.label}:</u></strong> {{${f.key}}}</p>`)
              .join('')}</div>`
          : getPitchDataHtml();

        const payload = {
          ...values,
          greetingText: toBase64(deltaToHtml(greetingDelta)),
          closingText: toBase64(deltaToHtml(closingDelta)),
          pitchData: toBase64(pitchBlockHtml),
          pitchBodyTemplateId: selectedPitchBodyId ?? null,
        };

        if (isEditing && id) {
          const response = await templateApi.updateTemplate(id, payload as any);
          if (response?.data?.success) {
            Swal.fire({
              title: 'Success!',
              text: 'Template updated successfully.',
              icon: 'success',
              confirmButtonText: 'OK',
            }).then(() => {
              navigate('/email?tab=templates');
            });
          }
        } else {
          const response = await templateApi.createTemplate(payload as any);
          if (response?.data?.success) {
            Swal.fire({
              title: 'Success!',
              text: 'Template created successfully.',
              icon: 'success',
              confirmButtonText: 'OK',
            }).then(() => {
              navigate('/email?tab=templates');
            });
          }
        }
      } catch (error) {
        handleError(error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Fetch pitch body templates for selector
  useEffect(() => {
    const fetchPitchBodies = async () => {
      try {
        const res = await pitchBodiesApi.getAll();
        if (res?.data?.success) {
          setPitchBodyTemplates(res.data.templates ?? []);
        }
      } catch (error) {
        console.error('Failed to fetch pitch body templates:', error);
      }
    };
    fetchPitchBodies();
  }, []);

  // Fetch template data if editing
  useEffect(() => {
    if (isEditing && id) {
      const fetchTemplate = async () => {
        try {
          setLoading(true);
          const response = await templateApi.getTemplate(id);
          if (response?.data?.success) {
            const template = response.data.template;

            formik.setValues({
              name: template.name || '',
              isDefault: template.isDefault || false,
            });

            setSelectedPitchBodyId(template.pitchBodyTemplateId ?? null);

            // Set editor contents with HTML (decode from base64 first)
            setTimeout(() => {
              const greetingEditor = greetingQuillRef.current?.getEditor();
              const closingEditor = closingQuillRef.current?.getEditor();

              if (greetingEditor && template.greetingText) {
                const greetingHtml = fromBase64(template.greetingText);
                greetingEditor.clipboard.dangerouslyPasteHTML(greetingHtml);
              }
              if (closingEditor && template.closingText) {
                const closingHtml = fromBase64(template.closingText);
                closingEditor.clipboard.dangerouslyPasteHTML(closingHtml);
              }
            }, 0);
          }
        } catch (error) {
          handleError(error);
          navigate('/email?tab=templates');
        } finally {
          setLoading(false);
        }
      };
      fetchTemplate();
    }
  }, [id, isEditing]);

  const insertMergeField = useCallback((field: string) => {
    const quillRef = activeField === 'greetingText' ? greetingQuillRef : closingQuillRef;
    const quill = quillRef.current?.getEditor();

    if (quill) {
      const range = quill.getSelection(true);
      quill.insertText(range.index, field);
      quill.setSelection(range.index + field.length, 0);
    }
  }, [activeField]);

  // No need to track changes - we get Delta directly from editor on submit
  const handleGreetingChange = useCallback(() => {}, []);
  const handleClosingChange = useCallback(() => {}, []);

  const handleGreetingFocus = useCallback(() => setActiveField('greetingText'), []);
  const handleClosingFocus = useCallback(() => setActiveField('closingText'), []);

  const handleCancel = () => {
    navigate('/email?tab=templates');
  };

  if (loading) {
    return (
      <div className="py-2 overflow-x-hidden sm:p-6" aria-busy="true">
        <Skeleton className="w-20 h-4 mb-4" />
        <Skeleton className="h-8 mb-6 w-72" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <SkeletonCard lines={2} />
            <SkeletonCard lines={5} />
            <SkeletonCard lines={5} />
          </div>
          <SkeletonCard lines={6} />
        </div>
      </div>
    );
  }

  const selectedTemplate = pitchBodyTemplates.find((pb) => pb.id === selectedPitchBodyId);
  const previewFields = selectedTemplate
    ? selectedTemplate.fields.filter((f) => f.enabled).sort((a, b) => a.order - b.order)
    : DEFAULT_PITCH_BLOCK_FIELDS.map((f, i) => ({ ...f, order: i, enabled: true }));

  return (
    // Shell supplies the page background, same as the other revamped pages.
    <div className="py-2 overflow-x-hidden sm:p-6">
      <div>
        <PageHeader
          breadcrumbs={[
            HOME_CRUMB,
            { label: 'Email', to: '/email' },
            { label: 'Email Templates', to: '/email?tab=templates' },
            { label: isEditing ? 'Edit Template' : 'Create Template' },
          ]}
          className="mb-6"
          title={isEditing ? 'Edit Template' : 'Create Template'}
          description="Design your email template with custom styling"
        />

        {/* Main Content */}
        <form onSubmit={formik.handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left Column - Form Fields */}
            <div className="lg:col-span-3 space-y-6">
              {/* Template Name Card */}
              <div className="p-6 bg-white border rounded-lg border-neutral-200">
                <label className="block mb-2 text-sm font-medium text-neutral-700">
                  Template Name <span className="text-danger-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="e.g., My Custom Template"
                  maxLength={50}
                  className="w-full p-3 text-sm border border-neutral-300 rounded-lg outline-none transition-colors focus:border-primary-500"
                />
                <div className="flex justify-between items-center mt-2">
                  {formik.touched.name && formik.errors.name ? (
                    <p className="text-xs text-danger-500">{formik.errors.name}</p>
                  ) : (
                    <span />
                  )}
                  <p className="text-xs text-neutral-500">{formik.values.name.length}/50</p>
                </div>
              </div>

              {/* Greeting Text Card */}
              <div className="p-6 bg-white border rounded-lg border-neutral-200">
                <label className="block mb-2 text-sm font-medium text-neutral-700">
                  Greeting Text <span className="text-danger-500">*</span>
                </label>
                <p className="text-xs text-neutral-500 mb-3">
                  This text appears at the beginning of your email, before the pitch data.
                </p>
                <div
                  className="editor-wrapper"
                  onClick={handleGreetingFocus}
                >
                  <ReactQuill
                    ref={greetingQuillRef}
                    theme="snow"
                    defaultValue=""
                    onChange={handleGreetingChange}
                    onFocus={handleGreetingFocus}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Enter greeting text before pitch data..."
                  />
                </div>
              </div>

              {/* Pitch Body Block — sits between Greeting and Closing */}
              <div className="p-6 bg-white border rounded-lg border-neutral-200">
                <label className="block mb-1 text-sm font-medium text-neutral-700">
                  Pitch Body
                </label>
                <p className="text-xs text-neutral-500 mb-3">
                  This block appears between your greeting and closing. Choose a custom layout or use the default.
                </p>

                {/* Selector. `appearance-none` plus our own chevron, so the
                    control matches the styled selects used elsewhere rather
                    than falling back to the browser's native arrow. */}
                <div className="relative mb-4">
                  <select
                    value={selectedPitchBodyId ?? ''}
                    onChange={(e) => setSelectedPitchBodyId(e.target.value || null)}
                    className="w-full py-2 pl-3 pr-9 text-sm text-black transition-colors bg-white border rounded-lg outline-none appearance-none cursor-pointer border-neutral-300 focus:border-primary-500"
                  >
                    <option value="">Default pitch data block</option>
                    {pitchBodyTemplates.map((pb) => (
                      <option key={pb.id} value={pb.id}>
                        {pb.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-2.5 top-1/2 text-neutral-400" />
                </div>

                {/* Visual preview of the selected pitch body fields */}
                <div className="border border-dashed border-primary-300 rounded-lg bg-neutral-50 px-4 py-3 space-y-1.5">
                  {previewFields.map((f) => (
                    <p key={f.key} className="text-xs text-neutral-500">
                      <span className="font-semibold text-neutral-700">{f.label}:</span>{' '}
                      <span className="font-mono text-primary-700">{`{{${f.key}}}`}</span>
                    </p>
                  ))}
                </div>

                {/* Hint */}
                {selectedPitchBodyId ? (
                  <p className="text-xs text-neutral-400 mt-3">
                    This is how your pitch data will appear in the email.{' '}
                    <Button
                      type="button"
                      variant="link"
                      size="inline"
                      onClick={() => navigate('/email?tab=pitch-bodies')}
                      className="text-xs"
                    >
                      Go to Pitch Bodies tab
                    </Button>{' '}
                    to change the order or toggle fields.
                  </p>
                ) : (
                  <p className="text-xs text-neutral-400 mt-3">
                    This is the fixed default block — all 11 fields are always included.{' '}
                    <Button
                      type="button"
                      variant="link"
                      size="inline"
                      onClick={() => navigate('/email?tab=pitch-bodies')}
                      className="text-xs"
                    >
                      Create a Pitch Body
                    </Button>{' '}
                    to customize which fields appear and in what order.
                  </p>
                )}
              </div>

              {/* Closing Text Card */}
              <div className="p-6 bg-white border rounded-lg border-neutral-200">
                <label className="block mb-2 text-sm font-medium text-neutral-700">
                  Closing Text <span className="text-danger-500">*</span>
                </label>
                <p className="text-xs text-neutral-500 mb-3">
                  This text appears at the end of your email, after the pitch data.
                </p>
                <div
                  className="editor-wrapper"
                  onClick={handleClosingFocus}
                >
                  <ReactQuill
                    ref={closingQuillRef}
                    theme="snow"
                    defaultValue=""
                    onChange={handleClosingChange}
                    onFocus={handleClosingFocus}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Enter closing text after pitch data..."
                  />
                </div>
              </div>

              {/* Options Card - only show for basic users, not admin */}
              {!isAdmin && (
                <div className="p-6 bg-white border rounded-lg border-neutral-200">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="isDefault"
                      name="isDefault"
                      checked={formik.values.isDefault}
                      onChange={formik.handleChange}
                      className="w-4 h-4 rounded text-primary-700 border-neutral-300 focus:border-primary-700"
                    />
                    <label htmlFor="isDefault" className="text-sm text-neutral-700">
                      Set as default template
                    </label>
                  </div>
                </div>
              )}

              {/* Email structure hint */}
              <div className="p-4 bg-primary-50 border border-primary-100 rounded-lg text-xs text-primary-800 space-y-1">
                <p className="font-semibold text-primary-900 mb-1">Email structure</p>
                <p>① Greeting Text &nbsp;→&nbsp; ② Artist Name - Release Title - Release Date (always shown) &nbsp;→&nbsp; ③ Pitch Body &nbsp;→&nbsp; ④ Closing Text</p>
                <p className="text-primary-700">Use merge fields in greeting/closing to personalize per contact.</p>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? 'Saving...' : isEditing ? 'Update Template' : 'Create Template'}
                </Button>
              </div>
            </div>

            {/* Right Column - Merge Fields */}
            <div className="lg:col-span-1">
              <div className="sticky p-4 bg-white border rounded-lg border-neutral-200 top-4">
                <h3 className="text-sm font-semibold text-black mb-2">Merge Fields</h3>
                <p className="text-xs text-neutral-600 mb-4">
                  Click to insert into {activeField === 'greetingText' ? 'greeting' : 'closing'} text
                </p>
                <div className="space-y-2">
                  {MERGE_FIELDS.map((field) => (
                    <button
                      key={field.key}
                      type="button"
                      onClick={() => insertMergeField(field.key)}
                      className="w-full text-left px-3 py-2.5 text-xs bg-neutral-50 border border-neutral-200 rounded-lg hover:bg-primary-50 hover:border-primary-300 transition-colors"
                    >
                      <div className="font-mono text-primary-700 font-medium">{field.key}</div>
                      <div className="text-neutral-600 mt-1">{field.label}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Custom Quill Styles */}
      <style>{`
        /* The wrapper draws the one and only focus indicator for the editor.
           :focus-within tracks the browser's real focus, so clicking away to any
           other control (the Pitch Body select, the name input) drops the ring
           automatically - no blur bookkeeping that can go stale. */
        .editor-wrapper {
          border: 1px solid var(--color-neutral-300); /* matching the app's inputs */
          border-radius: var(--radius-card);
          overflow: hidden;
          transition: border-color 0.15s ease;
        }
        .editor-wrapper:focus-within {
          border-color: var(--color-primary-500); /* same border-only focus as the app's inputs */
        }
        /* Quill's own borders are removed so the wrapper's outline is not doubled. */
        .editor-wrapper .ql-container {
          min-height: 150px;
          font-size: 14px;
          font-family: inherit;
          border: none;
        }
        .editor-wrapper .ql-editor {
          min-height: 150px;
        }
        .editor-wrapper .ql-editor:focus,
        .editor-wrapper .ql-editor:focus-visible,
        .editor-wrapper .ql-container:focus {
          outline: none;
        }
        .editor-wrapper .ql-toolbar {
          border: none;
          border-bottom: 1px solid var(--color-primary-200);
          background: var(--color-primary-50);
        }
        .editor-wrapper .ql-editor.ql-blank::before {
          font-style: normal;
          color: var(--color-neutral-400);
        }
        /* Toolbar controls: neutral at rest, brand on hover/active. Quill draws
           these as SVG strokes/fills, so colour has to be set on both. */
        .editor-wrapper .ql-snow .ql-stroke {
          stroke: var(--color-neutral-500);
        }
        .editor-wrapper .ql-snow .ql-fill {
          fill: var(--color-neutral-500);
        }
        .editor-wrapper .ql-snow .ql-picker-label {
          color: var(--color-neutral-500);
        }
        .editor-wrapper .ql-snow button:hover .ql-stroke,
        .editor-wrapper .ql-snow button.ql-active .ql-stroke,
        .editor-wrapper .ql-snow .ql-picker-item:hover,
        .editor-wrapper .ql-snow .ql-picker-label:hover {
          stroke: var(--color-primary-700);
          color: var(--color-primary-700);
        }
        .editor-wrapper .ql-snow button:hover .ql-fill,
        .editor-wrapper .ql-snow button.ql-active .ql-fill {
          fill: var(--color-primary-700);
        }
      `}</style>
    </div>
  );
};

export default TemplateEditor;
